import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";
import { RequestError } from "@/app/queries";
import type { Notebook, TaskResult } from "@/kernel/runner";

export type AssignmentView = components["schemas"]["AssignmentView"];
export type WorkspaceData = Omit<components["schemas"]["Workspace"], "notebook" | "results"> & { notebook: Notebook; results: Record<string, TaskResult> };
export type Version = components["schemas"]["Version"];

function fail(status: number, error: unknown): never {
  const e = error as { error?: string; message?: string } | undefined;
  throw new RequestError(status, e?.error ?? "unknown", e?.message ?? "Не удалось выполнить запрос.");
}

export const nbKeys = {
  assignment: (id: string) => ["assignment", id] as const,
  byStep: (stepId: string) => ["assignment", "by-step", stepId] as const,
  workspace: (id: string) => ["workspace", id] as const,
  versions: (id: string) => ["versions", id] as const,
};

const noRetry4xx = (n: number, err: unknown) => !(err instanceof RequestError && err.status >= 400 && err.status < 500) && n < 2;

export function useAssignment(id: string) {
  return useQuery({
    queryKey: nbKeys.assignment(id),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/assignments/{assignmentId}", { params: { path: { assignmentId: id } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
    staleTime: 10 * 60_000, // presigned-ссылки датасетов живут 15 минут
  });
}

/** Задание шага (для кнопки «открыть ноутбук» на шаге). 404 — у шага нет ноутбука. */
export function useAssignmentByStep(stepId: string, enabled = true) {
  return useQuery({
    queryKey: nbKeys.byStep(stepId),
    enabled,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/assignments/by-step/{stepId}", { params: { path: { stepId } } });
      if (!data) fail(response.status, error);
      return data;
    },
    retry: noRetry4xx,
  });
}

export function useWorkspace(id: string) {
  return useQuery({
    queryKey: nbKeys.workspace(id),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/assignments/{assignmentId}/workspace", { params: { path: { assignmentId: id } } });
      if (!data) fail(response.status, error);
      return data as unknown as WorkspaceData;
    },
    retry: noRetry4xx,
    staleTime: Infinity, // черновик живёт локально, сервер — только сохранение
  });
}

export async function saveWorkspace(id: string, notebook: Notebook, results: Record<string, TaskResult>): Promise<string> {
  const { data, error, response } = await api.PUT("/assignments/{assignmentId}/workspace", {
    params: { path: { assignmentId: id } },
    body: { notebook: notebook as unknown as Record<string, unknown>, results },
  });
  if (!data) fail(response.status, error);
  return data.updated_at;
}

export function useVersions(id: string) {
  return useQuery({
    queryKey: nbKeys.versions(id),
    queryFn: async () => {
      const { data, error, response } = await api.GET("/assignments/{assignmentId}/versions", { params: { path: { assignmentId: id } } });
      if (!data) fail(response.status, error);
      return data;
    },
  });
}

export function useSnapshot(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (note: string) => {
      const { data, error, response } = await api.POST("/assignments/{assignmentId}/versions", { params: { path: { assignmentId: id } }, body: { note } });
      if (!data) fail(response.status, error);
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: nbKeys.versions(id) }),
  });
}

export function useSendLink(id: string) {
  return useMutation({
    mutationFn: async () => {
      const { error, response } = await api.POST("/assignments/{assignmentId}/send-link", { params: { path: { assignmentId: id } } });
      if (response.status !== 204) fail(response.status, error);
    },
  });
}

/** Возврат из Colab: multipart вне типизированного клиента. */
export async function importNotebook(id: string, file: File): Promise<Version> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`/api/assignments/${id}/import`, { method: "POST", body: fd });
  const body = (await res.json()) as Version | { error: string; message: string };
  if (!res.ok) fail(res.status, body);
  return body as Version;
}

export const exportUrl = (id: string) => `/api/assignments/${id}/export`;

/** Есть ли среда, где LMS прогонит ноутбук сама: сейчас только браузер (Pyodide).
 *  python-cpu/gpu — «без раннера» до SP6: решается локально или в Colab, сдаётся без автопроверки. */
export const runnerAvailable = (runtime: string) => runtime === "pyodide-cpu";

export const RUNTIME_LABEL: Record<string, string> = {
  "pyodide-cpu": "CPU · в браузере",
  "python-cpu": "Python на сервере · CPU",
  "python-gpu": "Python на сервере · GPU",
};

/** Ноутбук отправленной версии (.ipynb) — для ментора и самого студента. */
export const submissionExportUrl = (submissionId: string) => `/api/submissions/${submissionId}/export`;
